import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import Script from 'next/script';
import getConfig from 'next/config';
import {
  getSessionFeatures,
  getSessionUser,
} from 'lib/store/session/selectors';
import { RS_CHATBOT } from 'lib/common/features';
import { getOrg } from 'lib/store/restaurants/selectors';
import { AppState } from 'lib/types/global';
import useI18n from 'lib/hooks/useI18n';

const { publicRuntimeConfig } = getConfig();

// eslint-disable-next-line camelcase
declare let embedded_svc: any;

const mapState = (state: AppState) => ({
  org: getOrg(state, state.dataFilters.orgId),
  user: getSessionUser(state),
});

const EinsteinChatBot = () => {
  const { org, user } = useSelector(mapState);
  const [hasLoaded, setHasLoaded] = useState(false);
  const features = useSelector(getSessionFeatures);
  const isFeatureEnabled = features.has(RS_CHATBOT);
  const i18n = useI18n();
  const initESW = function (gslbBaseURL?: string) {
    let defaultMinimizedText = 'Chat to Us'; //(Defaults to Chat with an Expert)
    let disabledMinimizedText = 'Chat Offline'; //(Defaults to Agent Offline)
    let loadingText = 'Loading';
    // eslint-disable-next-line camelcase
    embedded_svc.settings.displayHelpButton = true;
    if (org?.countryCode === 'IT') {
      // eslint-disable-next-line camelcase
      embedded_svc.settings.language = 'it';
      defaultMinimizedText = 'Chatta con noi';
      disabledMinimizedText = 'Chatta offline';
      loadingText = 'caricamento';
    } else if (
      org?.countryCode === 'FR' ||
      (org?.countryCode === 'BE' && i18n.language === 'fr')
    ) {
      // eslint-disable-next-line camelcase
      embedded_svc.settings.language = 'fr';
      defaultMinimizedText = 'Discutez avec nous';
      disabledMinimizedText = 'Chat hors ligne';
      loadingText = 'Chargement';
    } else if (org?.countryCode === 'BE' && i18n.language !== 'fr') {
      // eslint-disable-next-line camelcase
      embedded_svc.settings.displayHelpButton = false;
    }

    // eslint-disable-next-line camelcase
    embedded_svc.settings.defaultMinimizedText = defaultMinimizedText;
    // eslint-disable-next-line camelcase
    embedded_svc.settings.disabledMinimizedText = disabledMinimizedText;
    // eslint-disable-next-line camelcase
    embedded_svc.settings.loadingText = loadingText;

    // eslint-disable-next-line camelcase
    embedded_svc.settings.storageDomain = window.location.hostname;
    // eslint-disable-next-line camelcase
    embedded_svc.settings.avatarImgURL =
      publicRuntimeConfig.sfCbChatBotAvatarImgUrl;
    // eslint-disable-next-line camelcase
    embedded_svc.settings.chatbotAvatarImgURL =
      publicRuntimeConfig.sfCbBaseBotAgentContentUrl;
    // eslint-disable-next-line camelcase
    embedded_svc.settings.extraPrechatFormDetails = [
      {
        label: 'Full Name',
        value: user?.name,
        displayToAgent: true,
        transcriptFields: ['FullName__c'],
      },
      {
        label: 'Email',
        value: user?.email,
        displayToAgent: true,
        transcriptFields: ['EmailAddress__c'],
      },
      {
        label: 'Partner Short DRN',
        value: org?.shortDrn,
        displayToAgent: true,
        transcriptFields: ['Account_Short_DRN__c'],
      },
      {
        label: 'Partner Admin ID',
        value: org?.id,
        displayToAgent: true,
        transcriptFields: ['Account_Admin_ID__c'],
      },
      {
        label: 'Partner Country',
        value: org?.countryCode,
        displayToAgent: true,
        transcriptFields: ['Account_Country__c'],
      },
      {
        label: 'Referring Site',
        value: `${window.location.hostname}${window.location.pathname}`,
        displayToAgent: true,
        transcriptFields: ['ReferrerUri'],
      },
    ];

    // eslint-disable-next-line camelcase
    embedded_svc.settings.enabledFeatures = ['LiveAgent'];
    // eslint-disable-next-line camelcase
    embedded_svc.settings.entryFeature = 'LiveAgent';

    // eslint-disable-next-line camelcase
    embedded_svc.init(
      publicRuntimeConfig.sfCbBaseCoreUrl,
      publicRuntimeConfig.sfCbCommunityEndpointUrl,
      gslbBaseURL,
      publicRuntimeConfig.sfCbOrgId,
      publicRuntimeConfig.sfCbEswConfigDevName,
      {
        baseLiveAgentContentURL:
          publicRuntimeConfig.sfCbBaseLiveAgentContentUrl,
        deploymentId: publicRuntimeConfig.sfCbDeploymentId,
        buttonId: publicRuntimeConfig.sfCbButtonId,
        baseLiveAgentURL: publicRuntimeConfig.sfCbBaseLiveAgentUrl,
        eswLiveAgentDevName: publicRuntimeConfig.sfCbEswLiveAgentDevName,
        isOfflineSupportEnabled: false,
      },
    );
  };

  useEffect(() => {
    if (isFeatureEnabled && user && org && !hasLoaded) {
      setHasLoaded(true);
    }
  }, [user, org]);
  if (isFeatureEnabled && user && org && !hasLoaded) {
    return (
      <Script src={publicRuntimeConfig.sfCbEswMin} onLoad={() => initESW()} />
    );
  }
  return null;
};

export default EinsteinChatBot;
